import { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import { get } from 'lodash-es';

export const isValidSchema = (
  schema: JSONSchema7 | boolean | undefined | null
): schema is JSONSchema7 => {
  if (schema == null || typeof schema === 'boolean') return false;
  const keys = Object.keys(schema);

  // Empty object
  if (keys.length === 0) return false;

  // { "$schema": "..." }, also a valid empty schema
  if (keys.length === 1 && '$schema' in schema) return false;

  return true;
};

// TODO: Temporary for testing
export const defsForSchema = (
  def: JSONSchema7
): Record<string, () => unknown> => {
  const defs = Object.fromEntries([
    ...Object.entries(def.definitions ?? {}).map(([k, v]) => [
      `#/definitions/${k}`,
      () => defaultForSchemaDefinition(v, defs),
    ]),
    ...Object.entries(def.$defs ?? {}).map(([k, v]) => [
      `#/$defs/${k}`,
      () => defaultForSchemaDefinition(v, defs),
    ]),
  ]);
  return defs;
};

export const defaultForSchemaDefinitionBase = (
  def: JSONSchema7Definition,
  defs: Record<string, () => unknown>
): Record<string, unknown> => {
  if (typeof def === 'boolean') {
    console.error('Unknown edge case');
    return {};
  }

  if (def.type === 'object') {
    if (def.properties) {
      return Object.fromEntries(
        Object.entries(def.properties)
          .filter(
            ([k]) => def.required === undefined || def.required.includes(k)
          )
          .map(([k, v]) => [k, defaultForSchemaDefinition(v, defs)])
      );
    }
    return {};
  }

  return {};
};

// TODO: Temporary for testing
export const defaultForSchemaDefinition = (
  def: JSONSchema7Definition,
  defs: Record<string, (defs: unknown) => unknown>
): unknown => {
  if (typeof def === 'boolean') {
    console.error('Unknown edge case');
    return {};
  }

  if (def.$ref) {
    const resolve = defs[def.$ref];
    if (resolve) return resolve(defs);
    return {};
  }

  if (def.type === 'object') {
    if (def.additionalProperties) {
      return {};
    }
    if (def.properties) {
      return Object.fromEntries(
        Object.entries(def.properties)
          .filter(
            ([k]) => def.required === undefined || def.required.includes(k)
          )
          .map(([k, v]) => [k, defaultForSchemaDefinition(v, defs)])
      );
    }
    return {};
  }

  if (def.type === 'array') {
    return [];
  }

  if (def.type === 'string') {
    return def.default ?? '';
  }

  if (def.type === 'number' || def.type === 'integer') {
    return def.default ?? 0;
  }

  if (def.type === 'boolean') {
    return def.default ?? false;
  }

  if (def.anyOf) {
    const first = def.anyOf[0];
    if (first) return defaultForSchemaDefinition(first, defs);
  }

  if (def.oneOf) {
    const first = def.oneOf[0];
    if (first) return defaultForSchemaDefinition(first, defs);
  }

  // Cases remaining:
  // - type is array of types
  // - null
  return {};
};

export const isJSONSchema7 = (
  schema: JSONSchema7Definition | undefined | null
): schema is JSONSchema7 => {
  return typeof schema === 'object' && schema != null;
};

export const pointerToSegments = (path: string | null | undefined) =>
  (path ?? '')
    .split('/')
    .map((segment: string) => segment?.replace(/~1/g, '/').replace(/~0/g, '~'));

export const segmentsToPointer = (segments: string[]): string =>
  ['#', ...segments]
    .map((i) => i.replaceAll('~', '~0').replaceAll('/', '~1'))
    .join('/');

export const resolveRef = (
  root: JSONSchema7Definition | undefined,
  path: string | null | undefined
): JSONSchema7 | null => {
  const segments = pointerToSegments(path).filter((x) => !!x && x !== '#');
  if (segments.length === 0 || !isJSONSchema7(root)) return null;
  let current = root;
  for (const segment of segments) {
    const next = get(current, segment);
    if (!next) return null;
    current = next;
  }

  return current;
};

export const removeAtPointers = (values: unknown, paths: string[]): unknown => {
  const newValues =
    'structuredClone' in self
      ? structuredClone(values)
      : JSON.parse(JSON.stringify(values));

  for (const path of paths) {
    const segments = pointerToSegments(path).filter((x) => !!x && x !== '#');

    let current = newValues;
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      if (i === segments.length - 1) {
        if (Array.isArray(current)) {
          current[+segment] = Symbol.for('empty');
        } else {
          delete current[segment];
        }
        break;
      }
      current = current[segment];
    }
  }

  return JSON.parse(
    JSON.stringify(newValues, (_, value) => {
      if (Array.isArray(value))
        return value.filter((v) => v !== Symbol.for('empty'));
      if (value === Symbol.for('empty')) return undefined;
      return value;
    })
  );
};

export const resolveSchemaComposition = (
  schema: JSONSchema7Definition,
  root: JSONSchema7Definition | null | undefined
): JSONSchema7 | null => {
  if (!isJSONSchema7(schema) || !isJSONSchema7(root)) return null;

  if (schema.allOf) {
    const first = schema.allOf[0];
    if (isJSONSchema7(first) && schema.allOf.length === 1) {
      return {
        ...schema,
        ...(resolveRef(root, first.$ref) ?? first),
      };
    }
  }

  if (schema.oneOf) {
    const first = schema.oneOf[0];
    if (isJSONSchema7(first)) {
      return {
        ...schema,
        ...(resolveRef(root, first.$ref) ?? first),
      };
    }
  }

  if (schema.anyOf) {
    const first = schema.anyOf[0];
    if (isJSONSchema7(first)) {
      return {
        ...schema,
        ...(resolveRef(root, first.$ref) ?? first),
      };
    }
  }

  return schema;
};

export const isConfigFieldPrompt = (schema: JSONSchema7Definition) => {
  return (
    typeof schema === 'object' &&
    'langgraph_type' in schema &&
    schema.langgraph_type === 'prompt'
  );
};

export const getConfigFieldNodes = (
  schema: JSONSchema7Definition | null | undefined
): string[] | undefined => {
  if (
    schema == null ||
    typeof schema !== 'object' ||
    !('langgraph_nodes' in schema)
  )
    return undefined;
  return schema.langgraph_nodes as string[];
};

export const getSchemaProperties = (
  schema: JSONSchema7Definition | null | undefined
) => {
  if (schema == null || typeof schema !== 'object' || !('properties' in schema))
    return undefined;
  return schema.properties;
};
